import { Link, Outlet, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { listWarehouses, listInventoryMaps, getStructureSummary } from "../api/rest";
import { EmptyState } from "../components/EmptyState";
import { Tabs, type TabItem, type TabStatus } from "../app/layout/Tabs";
import { useSiteId } from "../hooks/useSiteId";
import { unsavedTabGuard } from "../hooks/useUnsavedGuard";
import css from "./WarehouseAdminPage.module.css";

/**
 * One page per warehouse (spec 2026-08-11). The six controls that used to sit in
 * the list row were grouped by tool; a warehouse has three reference artifacts —
 * the GLB, the 2D plan, the SLAM map — and each needs ingest AND placement, so
 * the tabs group by the thing being configured and keep both halves together.
 *
 * The ADR 229 cross-site guard lives HERE, not per tab: the route names the
 * warehouse while the store names the site, and every tab would 400 the same way
 * on a mismatched pair. One guard, checked once, before any tab renders.
 */
export function WarehousePage() {
  const { t } = useTranslation();
  const siteId = useSiteId();
  const { warehouseId = "" } = useParams();

  const { data: warehouses = [], isSuccess: warehousesLoaded } = useQuery({
    queryKey: ["warehouses", siteId],
    queryFn: () => listWarehouses(siteId),
  });
  const warehouse = warehouses.find((w) => w.id === warehouseId);
  const crossSite = warehousesLoaded && !warehouse;

  // Same query keys the tabs themselves use, so the dots cost no extra request.
  const { data: maps = [], isSuccess: mapsLoaded } = useQuery({
    queryKey: ["inventory-maps", siteId],
    queryFn: () => listInventoryMaps(siteId),
    retry: 0,
  });
  const { data: summary, isSuccess: summaryLoaded } = useQuery({
    queryKey: ["structure-summary", siteId, warehouseId],
    queryFn: () => getStructureSummary(siteId, warehouseId),
    enabled: !!warehouseId,
    retry: 0,
  });

  if (crossSite) {
    return (
      <div className={css.root}>
        <EmptyState
          title={t("structure.crossSite.title")}
          description={t("structure.crossSite.desc", { warehouseId, siteId })}
          action={{ to: "/admin/warehouses", label: t("structure.crossSite.action") }}
        />
      </div>
    );
  }

  const status = (loaded: boolean, present: boolean): TabStatus =>
    !loaded ? "unknown" : present ? "present" : "absent";
  const label = (key: string, s: TabStatus) =>
    s === "unknown" ? undefined : t(`warehouse.status.${key}.${s}`);

  const modelStatus = status(warehousesLoaded, !!warehouse?.modelKey);
  const mapStatus = status(mapsLoaded, maps.some((m) => m.warehouseId === warehouseId));
  const structureStatus = status(summaryLoaded, (summary?.locations ?? 0) > 0);

  const items: TabItem[] = [
    { to: "model", label: t("warehouse.tabs.model"), status: modelStatus, statusLabel: label("model", modelStatus) },
    { to: "map", label: t("warehouse.tabs.map"), status: mapStatus, statusLabel: label("map", mapStatus) },
    { to: "structure", label: t("warehouse.tabs.structure"), status: structureStatus, statusLabel: label("structure", structureStatus) },
  ];

  return (
    <div className={css.root}>
      <div className={css.header}>
        <div>
          <div className={css.title}>{warehouse?.name ?? warehouseId}</div>
          <div className={css.siteTag}>{siteId} · {warehouseId}</div>
        </div>
        <Link className={css.backLink} to="/admin/warehouses">{t("warehouse.back")}</Link>
      </div>

      <Tabs items={items} ariaLabel={t("warehouse.tabs.aria")} guard={unsavedTabGuard}>
        <Outlet />
      </Tabs>
    </div>
  );
}
